import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const partners = [
  { name: 'Talk Cargo', image: '/talkcargo.png' },
  { name: 'Pacific AirHub', image: '/pairhub.png' },
  { name: 'Fiordland Helicopters', image: 'https://www.fiordlandhelicopters.co.nz/wp-content/themes/fiordlandhelicopters2022/images/fiordland-helicopters-logo.svg' },
  { name: 'Aeronet Software', image: '/aeronet.png' },
  { name: 'NZ Jet', image: '/NZJET.png' },
  { name: 'Beck Helicopters', image: '/beckheli.png' },
];

const PartnerCarousel = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.3 });

  return (
    <section
      ref={ref}
      className="py-24 relative overflow-hidden bg-gradient-to-br from-midnight-navy via-midnight-navy/95 to-midnight-navy"
    >
      {/* Background Blur/Gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-aero-blue/5 via-transparent to-aero-blue/5"></div>
        <motion.div
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-aero-blue/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.6, 0.3, 0.6] }}
          transition={{ duration: 8, repeat: Infinity, delay: 4 }}
        />
      </div>

      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 1 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-8 tracking-tight">
            Trusted <span className="bg-gradient-to-r from-aero-blue to-cyan-400 bg-clip-text text-transparent">Partners</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-light">
            We collaborate with leading global suppliers and hold certifications from key aerospace bodies, 
            ensuring quality and compliance across North America and the Pacific region.
          </p>
        </motion.div>

        {/* Moving Carousel Row */}
        <div className="relative overflow-hidden mb-20">
          <motion.div
            className="flex space-x-6"
            animate={{ x: ['0%', '-100%'] }}
            transition={{ duration: 20, ease: 'linear', repeat: Infinity }}
            style={{ width: 'max-content' }}
          >
            {[...partners, ...partners].map((partner, index) => (
              <div key={`${partner.name}-${index}`} className="flex-shrink-0 w-[300px] h-[200px]">
                <img
                  src={partner.image}
                  alt={partner.name}
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
            ))}
          </motion.div>
        </div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-aero-blue/5 via-transparent to-cyan-400/5 opacity-50" />
          <div className="relative z-10">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">
              Global Network & Distribution
            </h3>
            <p className="text-gray-300 text-center leading-relaxed max-w-2xl mx-auto mb-8">
              Our logistics network and quality controls ensure speed and compliance, wherever you are. 
              We maintain strategic partnerships that enable us to deliver exceptional service across multiple regions.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { value: '16+', label: 'Strategic Partners', color: 'text-aero-blue' },
                { value: '24/7', label: 'Support Network', color: 'text-cyan-400' },
                { value: 'Global', label: 'Coverage', color: 'text-emerald-400' }
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  className="text-center"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                >
                  <div className={`text-3xl font-bold ${stat.color} mb-2`}>{stat.value}</div>
                  <div className="text-gray-300 text-sm font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PartnerCarousel;
